import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, 
  Database, 
  Shield, 
  Mail, 
  Server, 
  Globe,
  Clock,
  Users,
  FileText,
  Bell
} from "lucide-react";

export default function SystemSettingsScreen() {
  const [emailSettings, setEmailSettings] = useState({
    smtpServer: "smtp.redwood.com",
    smtpPort: "587",
    smtpUsername: "notifications@redwood.com",
    enableSSL: true,
    enableNotifications: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: "60",
    passwordExpiry: "90",
    enableTwoFactor: false,
    enableAuditLog: true,
    enableSSOIntegration: false
  });

  const [systemSettings, setSystemSettings] = useState({
    enablePublicAssessments: true,
    enableProjectSharing: true,
    maxProjectsPerUser: "10",
    dataRetentionDays: "365",
    enableBackups: true,
    backupFrequency: "daily"
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-white">System Settings</h1>
          <p className="text-gray-400">Configure global system preferences and security options</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Configuration */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Email Configuration
            </CardTitle>
            <CardDescription className="text-gray-400">
              Configure SMTP settings for system notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">SMTP Server</Label>
              <Input
                value={emailSettings.smtpServer}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtpServer: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Port</Label>
                <Input
                  value={emailSettings.smtpPort}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtpPort: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Username</Label>
                <Input
                  value={emailSettings.smtpUsername}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtpUsername: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable SSL</Label>
              <Switch
                checked={emailSettings.enableSSL}
                onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, enableSSL: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Email Notifications</Label>
              <Switch
                checked={emailSettings.enableNotifications}
                onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, enableNotifications: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Security Settings
            </CardTitle>
            <CardDescription className="text-gray-400">
              Configure authentication and security policies
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Session Timeout (minutes)</Label>
                <Input
                  value={securitySettings.sessionTimeout}
                  onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Password Expiry (days)</Label>
                <Input
                  value={securitySettings.passwordExpiry}
                  onChange={(e) => setSecuritySettings(prev => ({ ...prev, passwordExpiry: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Two-Factor Authentication</Label>
              <Switch
                checked={securitySettings.enableTwoFactor}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, enableTwoFactor: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Audit Logging</Label>
              <Switch
                checked={securitySettings.enableAuditLog}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, enableAuditLog: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable SSO Integration</Label>
              <Switch
                checked={securitySettings.enableSSOIntegration}
                onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, enableSSOIntegration: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* System Configuration */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              System Configuration
            </CardTitle>
            <CardDescription className="text-gray-400">
              Configure global system behavior and limits
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Public Assessments</Label>
              <Switch
                checked={systemSettings.enablePublicAssessments}
                onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, enablePublicAssessments: checked }))}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Project Sharing</Label>
              <Switch
                checked={systemSettings.enableProjectSharing}
                onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, enableProjectSharing: checked }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Max Projects Per User</Label>
                <Input
                  value={systemSettings.maxProjectsPerUser}
                  onChange={(e) => setSystemSettings(prev => ({ ...prev, maxProjectsPerUser: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Data Retention (days)</Label>
                <Input
                  value={systemSettings.dataRetentionDays}
                  onChange={(e) => setSystemSettings(prev => ({ ...prev, dataRetentionDays: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-white">Enable Automated Backups</Label>
              <Switch
                checked={systemSettings.enableBackups}
                onCheckedChange={(checked) => setSystemSettings(prev => ({ ...prev, enableBackups: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* System Status */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              System Status
            </CardTitle>
            <CardDescription className="text-gray-400">
              Monitor system health and performance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-white">Database Status</span>
              <Badge className="bg-green-600 text-white">Connected</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white">Authentication Service</span>
              <Badge className="bg-green-600 text-white">Online</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white">Email Service</span>
              <Badge className="bg-green-600 text-white">Operational</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white">Backup Service</span>
              <Badge className="bg-green-600 text-white">Active</Badge>
            </div>
            <Separator className="bg-gray-600" />
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Active Users</span>
                <span className="text-white">47</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Total Projects</span>
                <span className="text-white">128</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">System Uptime</span>
                <span className="text-white">99.8%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button className="bg-primary text-black hover:bg-cyan-300">
          Save All Settings
        </Button>
        <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-700">
          Reset to Defaults
        </Button>
        <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-700">
          Export Configuration
        </Button>
      </div>
    </div>
  );
}